import { Currency } from '../types/global/helper';

// Currency symbols mapping
export const CURRENCY_SYMBOLS: Record<string, string> = {
    USD: '$',
    NGN: '₦',
    EUR: '€',
    GBP: '£',
    GHS: '₵',
    ZAR: 'R',
};

// Default currency
export const DEFAULT_CURRENCY: Currency = 'USD';

// Currency decimal places
export const CURRENCY_DECIMAL_PLACES: Record<string, number> = {
    USD: 2,
    NGN: 2,
    EUR: 2,
    GBP: 2,
    GHS: 2,
    ZAR: 2,
};

// Format currency with appropriate symbol and decimal places
export const formatCurrency = (
    amount: number = 0,
    currencyCode: string = DEFAULT_CURRENCY,
    cents?: boolean,
): string => {
    const code = (currencyCode || DEFAULT_CURRENCY).toUpperCase();
    const symbol = CURRENCY_SYMBOLS[code] || CURRENCY_SYMBOLS[DEFAULT_CURRENCY];
    const decimalPlaces = CURRENCY_DECIMAL_PLACES[code] || 2;

    let currAmount = amount;
    if (cents) {
        currAmount = amount / 100;
    }

    const formattedAmount = currAmount.toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });

    return `${symbol}${formattedAmount}`;
};

export const formatCurrencyWithAbbrevation = (amount: number = 0, currencyCode: string = DEFAULT_CURRENCY): string => {
    const code = (currencyCode || DEFAULT_CURRENCY).toUpperCase();
    const symbol = CURRENCY_SYMBOLS[code] || CURRENCY_SYMBOLS[DEFAULT_CURRENCY];
    const decimalPlaces = CURRENCY_DECIMAL_PLACES[code] ?? 2;

    let shortAmount: string;
    const absAmount = Math.abs(amount); // for correct comparison

    if (absAmount >= 1_000_000_000) {
        shortAmount = (amount / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    } else if (absAmount >= 1_000_000) {
        shortAmount = (amount / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (absAmount >= 1_000) {
        shortAmount = (amount / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    } else {
        shortAmount = amount.toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        });
        return `${symbol}${shortAmount}`;
    }

    return `${symbol}${shortAmount}`;
};

export const currencySymbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CAD: 'C$',
    AUD: 'A$',
    CHF: 'CHF',
    CNY: '¥',
    INR: '₹',
    BRL: 'R$',
    MXN: 'MX$',
    KRW: '₩',
    SGD: 'S$',
    HKD: 'HK$',
    NZD: 'NZ$',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    CZK: 'Kč',
    HUF: 'Ft',
    RUB: '₽',
    TRY: '₺',
    ZAR: 'R',
    NGN: '₦', // Nigerian Naira
    GHS: 'GH₵', // Ghanaian Cedi
    KES: 'KSh', // Kenyan Shilling
    UGX: 'USh', // Ugandan Shilling
    TZS: 'TSh', // Tanzanian Shilling
    ETB: 'Br', // Ethiopian Birr
    EGP: 'E£', // Egyptian Pound
    MAD: 'MAD', // Moroccan Dirham
    THB: '฿', // Thai Baht
    MYR: 'RM', // Malaysian Ringgit
    IDR: 'Rp', // Indonesian Rupiah
    PHP: '₱', // Philippine Peso
    VND: '₫', // Vietnamese Dong
    BDT: '৳', // Bangladeshi Taka
    PKR: '₨', // Pakistani Rupee
    LKR: 'Rs', // Sri Lankan Rupee
    NPR: '₨', // Nepalese Rupee
    MMK: 'K', // Myanmar Kyat
    KHR: '៛', // Cambodian Riel
    LAK: '₭', // Lao Kip
    MNT: '₮', // Mongolian Tugrik
    KZT: '₸', // Kazakhstani Tenge
    UZS: "so'm", // Uzbekistani Som
    TJS: 'ЅM', // Tajikistani Somoni
    KGS: 'с', // Kyrgyzstani Som
    TMT: 'T', // Turkmenistani Manat
    AZN: '₼', // Azerbaijani Manat
    GEL: '₾', // Georgian Lari
    AMD: '֏', // Armenian Dram
    BYN: 'Br', // Belarusian Ruble
    MDL: 'L', // Moldovan Leu
    UAH: '₴', // Ukrainian Hryvnia
    BGN: 'лв', // Bulgarian Lev
    RON: 'lei', // Romanian Leu
    HRK: 'kn', // Croatian Kuna
    RSD: 'дин', // Serbian Dinar
    BAM: 'KM', // Bosnia and Herzegovina Convertible Mark
    ALL: 'L', // Albanian Lek
    MKD: 'ден', // Macedonian Denar
};

export const supportedCurrency = [
    {
        value: 'USD',
        label: '$',
        country: 'United States',
    },
    {
        value: 'EUR',
        label: '€',
        country: 'European Union',
    },
    {
        value: 'GBP',
        label: '£',
        country: 'United Kingdom',
    },
    {
        value: 'JPY',
        label: '¥',
        country: 'Japan',
    },
    {
        value: 'CAD',
        label: '$',
        country: 'Canada',
    },
    {
        value: 'AUD',
        label: '$',
        country: 'Australia',
    },
    {
        value: 'CNY',
        label: '¥',
        country: 'China',
    },
    {
        value: 'INR',
        label: '₹',
        country: 'India',
    },
    {
        value: 'BRL',
        label: 'R$',
        country: 'Brazil',
    },
    {
        value: 'MXN',
        label: '$',
        country: 'Mexico',
    },
    {
        value: 'XCD',
        label: '$',
        country: 'Eastern Caribbean',
    },
    {
        value: 'JMD',
        label: 'J$',
        country: 'Jamaica',
    },
    {
        value: 'TTD',
        label: 'TT$',
        country: 'Trinidad and Tobago',
    },
    {
        value: 'BBD',
        label: 'Bds$',
        country: 'Barbados',
    },
    {
        value: 'BSD',
        label: 'B$',
        country: 'Bahamas',
    },
    {
        value: 'CUP',
        label: '$',
        country: 'Cuba',
    },
    {
        value: 'DOP',
        label: 'RD$',
        country: 'Dominican Republic',
    },
    {
        value: 'HTG',
        label: 'G',
        country: 'Haiti',
    },
    {
        value: 'AWG',
        label: 'ƒ',
        country: 'Aruba',
    },
    {
        value: 'ANG',
        label: 'ƒ',
        country: 'Netherlands Antilles',
    },
    {
        value: 'NGN',
        label: '₦',
        country: 'Nigeria',
    },
    {
        value: 'ZAR',
        label: 'R',
        country: 'South Africa',
    },
    {
        value: 'KES',
        label: 'KSh',
        country: 'Kenya',
    },
    {
        value: 'GHS',
        label: '₵',
        country: 'Ghana',
    },
    {
        value: 'XOF',
        label: 'CFA',
        country: 'West African CFA countries',
    },
    {
        value: 'XAF',
        label: 'FCFA',
        country: 'Central African CFA countries',
    },
    {
        value: 'EGP',
        label: '£',
        country: 'Egypt',
    },
    {
        value: 'MAD',
        label: 'د.م.',
        country: 'Morocco',
    },
    {
        value: 'TND',
        label: 'د.ت',
        country: 'Tunisia',
    },
    {
        value: 'DZD',
        label: 'د.ج',
        country: 'Algeria',
    },
    {
        value: 'ETB',
        label: 'Br',
        country: 'Ethiopia',
    },
    {
        value: 'TZS',
        label: 'TSh',
        country: 'Tanzania',
    },
    {
        value: 'UGX',
        label: 'USh',
        country: 'Uganda',
    },
    {
        value: 'MWK',
        label: 'MK',
        country: 'Malawi',
    },
    {
        value: 'MZN',
        label: 'MT',
        country: 'Mozambique',
    },
    {
        value: 'BWP',
        label: 'P',
        country: 'Botswana',
    },
    {
        value: 'LSL',
        label: 'L',
        country: 'Lesotho',
    },
    {
        value: 'NAD',
        label: '$',
        country: 'Namibia',
    },
    {
        value: 'SCR',
        label: '₨',
        country: 'Seychelles',
    },
    {
        value: 'RWF',
        label: 'FRw',
        country: 'Rwanda',
    },
    {
        value: 'SDG',
        label: '£',
        country: 'Sudan',
    },
    {
        value: 'SSP',
        label: '£',
        country: 'South Sudan',
    },
    {
        value: 'GNF',
        label: 'FG',
        country: 'Guinea',
    },
    {
        value: 'SLL',
        label: 'Le',
        country: 'Sierra Leone',
    },
    {
        value: 'LRD',
        label: '$',
        country: 'Liberia',
    },
    {
        value: 'MGA',
        label: 'Ar',
        country: 'Madagascar',
    },
    {
        value: 'CVE',
        label: '$',
        country: 'Cape Verde',
    },
];
